import type { SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, Input, ViewContainerRef } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject, take, takeUntil } from 'rxjs';

import { UserSelectors } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import { AnalyticsViewContext, provideAnalyticsViewContext } from '@recruitee/analytics';
import { FollowButtonComponent } from '@recruitee/candidate-ui';
import { filterBoolean } from '@recruitee/common';
import { RequisitionsActions } from '@recruitee/requisitions-data';
import type { Requisition, RequisitionAssignment } from '@recruitee/requisitions-types';
import { AdminsAvatarsComponent, BoxComponent } from '@recruitee/user-interface';

import { RequisitionsHeaderComponent } from '../../../components/requisitions-header/requisitions-header.component';
import { RequisitionsTabsComponent } from '../../../components/requisitions-tabs/requisitions-tabs.component';
import { RequisitionsGeneralFacade } from '../../../facades/single-requisition/requisitions-general.facade';
import { TeamMembersFacade } from '../../../facades/single-requisition/team-members.facade';
import type { ApprovalRequestWithRequester } from '../../../requisitions.types';
import { RequisitionsSharedActionsService } from '../../../services/requisitions-shared-actions.service';
import { RequisitionsTeamMembersService } from '../../../services/requisitions-team-members.service';

@Component({
  selector: 'rt-requisitions-toolbar-container',
  templateUrl: './requisitions-toolbar-container.component.html',
  styleUrls: ['./requisitions-toolbar-container.component.less'],
  imports: [
    AdminsAvatarsComponent,
    BoxComponent,
    RequisitionsHeaderComponent,
    RequisitionsTabsComponent,
    FollowButtonComponent,
  ],
  providers: [provideAnalyticsViewContext(AnalyticsViewContext.Requisition)],
})
export class RequisitionsToolbarContainerComponent {
  @Input() public requisition: Requisition;
  @Input() public assignments: RequisitionAssignment[] = [];

  public assignedAdmins: User[] = [];
  public isTitleEditable = false;
  public canEditMembers = false;
  public titleSaved$ = new Subject<void>();
  public isArchived: boolean;
  public approvalRequestWithRequester: ApprovalRequestWithRequester;
  private allAdmins: User[] = [];

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private actions$: Actions,
    private requisitionsTeamMembersService: RequisitionsTeamMembersService,
    private teamMembersFacade: TeamMembersFacade,
    private requisitionsGeneralFacade: RequisitionsGeneralFacade,
    private store: Store,
    private requisitionsSharedActionsService: RequisitionsSharedActionsService,
    private viewContainerRef: ViewContainerRef,
  ) {
    this.store
      .select(UserSelectors.entities())
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(admins => {
        this.allAdmins = admins;
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.assignments?.currentValue) {
      this.setAssignedAdmins();
    }

    if (changes.requisition?.currentValue) {
      this.isArchived = !!this.requisition.archivedAt;
      if (this.requisition.approvalRequest) {
        this.setApprovalRequestWithRequester();
      }
    }
    this.isTitleEditable = this.requisitionsGeneralFacade.isTitleEditable;
    this.canEditMembers = this.teamMembersFacade.canEditTeamMembers;
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public editTeamMembers(): void {
    this.requisitionsTeamMembersService.editTeamMembers(
      this.assignments,
      this.requisition.id,
      this.viewContainerRef,
    );
  }

  public followClick(): void {
    this.requisition.followed
      ? this.requisitionsSharedActionsService.unfollow(this.requisition.id)
      : this.requisitionsSharedActionsService.follow(this.requisition.id);
  }

  public updateTitle(title: string) {
    this.store.dispatch(
      RequisitionsActions.updateTitle({
        payload: { data: { id: this.requisition.id, title } },
      }),
    );
    this.actions$.pipe(ofType(RequisitionsActions.updateTitleSuccess), take(1)).subscribe(() => {
      this.titleSaved$.next();
    });
  }

  private setApprovalRequestWithRequester(): void {
    const requester = this.allAdmins.find(
      admin => admin.id === this.requisition.approvalRequest?.requesterId,
    );

    this.approvalRequestWithRequester = {
      ...this.requisition.approvalRequest!,
      requester,
    };
  }

  private setAssignedAdmins(): void {
    this.assignedAdmins = this.assignments.reduce(
      (acc, assignment) => {
        const admin = this.allAdmins.find(admin => admin.id === assignment.adminId);
        if (admin) acc.push(admin);
        return [...acc];
      },
      <User[]>[],
    );
  }
}
